import React, {
  CSSProperties,
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "@/components/ui/data-table-pagination2";
import {
  CreativeReportAd,
  CreativeReportColumn,
  CreativeReportRow,
  CreativeReportMediaItem,
  ServerPaginationState,
} from "../../types";
import {
  Column,
  ColumnDef,
  PaginationState,
  Row,
  SortingState,
  Updater,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Image } from "lucide-react";

import { getFirstMedia } from "../CreativeReportGroupCards";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Portal } from "@radix-ui/react-tooltip";
import { IconAlertTriangle, IconDatabase } from "@tabler/icons-react";
import MarkdownFormatter from "@/components/common/markdown/MarkdownFormatter";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import MediaIcon from "@/components/common/MediaIcon";
import { useGlobalStore } from "@/GlobalStore";
import { formatDateRange } from "@/utils/dateUtils";
import PercentageText from "@/screens/creatives/components/table/PercentageText";
import { formatNumberWithPrefixSuffix } from "@/screens/creatives/components/table/cellFormatters";
import { SqButton } from "@/components/squircle-ui/button";
import CopyButton from "@/components/common/CopyButton";
import {
  RiArrowDownLine,
  RiArrowLeftWideLine,
  RiArrowRightWideLine,
  RiArrowUpDownLine,
  RiArrowUpLine,
  RiCloseLine,
  RiSearchLine,
} from "@remixicon/react";
import { LuLoaderCircle } from "react-icons/lu";
import { CreativeReportMetadata } from "@/types";

const SPACER = "spacer";

const RenderPinnedCell = ({
  text,
  mediaItem,
  clickAction,
  mediaType,
  total_ads,
}: {
  text: React.ReactNode;
  mediaItem: CreativeReportMediaItem;
  clickAction: () => void;
  mediaType: string;
  total_ads?: number;
}) => {
  const [hasImageError, setHasImageError] = useState(false);

  const getTextValue = (): string => {
    if (typeof text === "string") return text;
    if (React.isValidElement(text) && "getValue" in text.props) {
      return String((text.props as { getValue: () => unknown }).getValue());
    }
    return String(text);
  };

  return (
    <div className="flex gap-3">
      <div
        role="button"
        className="relative flex items-center bg-[#EDEFF3] justify-center min-w-[60px] w-[60px] h-[60px] overflow-hidden rounded-[8px] cursor-pointer"
        onClick={clickAction}
      >
        {mediaItem && mediaItem.url && !hasImageError ? (
          mediaItem.type === "video" ? (
            <img
              src={mediaItem.thumbnail}
              alt="Image"
              loading="lazy"
              className="w-full h-full object-cover"
              onError={() => setHasImageError(true)}
            />
          ) : (
            <img
              src={mediaItem.url}
              alt={"Image"}
              loading="lazy"
              className="w-full h-full object-cover"
              onError={() => setHasImageError(true)}
            />
          )
        ) : (
          <Image size={32} strokeWidth={1.5} className="text-[#B5BCCB]" />
        )}
        <div className="absolute inset-0 flex items-end justify-end p-[6px] opacity-100 group-hover/thumbnail:opacity-0">
          <MediaIcon type={mediaType as "image" | "video" | "mixed"} />
        </div>
      </div>
      <div className="w-full overflow-hidden flex flex-col gap-2">
        <div className="w-full overflow-hidden group flex items-center gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="flex-1 overflow-hidden">
                <p
                  className={`whitespace-nowrap text-[12px] truncate text-left text-brand-primary-new/80 group-hover/row:text-brand-primary-new font-medium leading-5 mt-1`}
                >
                  {text}
                </p>
              </TooltipTrigger>
              <Portal>
                <TooltipContent
                  side="bottom"
                  align="start"
                  className={`text-[12px] max-w-[340px] text-left z-[60]`}
                  avoidCollisions
                  alignOffset={25}
                  collisionPadding={10}
                >
                  <p className="text-[12px] text-left text-white break-all">
                    {getTextValue()}
                  </p>
                </TooltipContent>
              </Portal>
            </Tooltip>
            <CopyButton text={text} />
          </TooltipProvider>
        </div>
        <div className="flex gap-2 items-center">
          {total_ads ? (
            <div className="text-[12px]">
              {total_ads} {total_ads === 1 ? "Ad" : "Ads"}
            </div>
          ) : (
            ""
          )}
        </div>
      </div>
    </div>
  );
};

interface CreativeTableColumnMeta {
  align: "left" | "right" | "center";
  label: string;
  type: string;
  suffix: string;
  prefix: string;
  messages?: {
    info?: string;
    warning?: string;
  };
}

interface AdsCreativeTableProps {
  data: CreativeReportAd[];
  columns: CreativeReportColumn[];
  setGroupSheetOpen?: (open: boolean) => void;
  setSelectedAdGroup?: (adGroup: CreativeReportAd) => void;
  setPreviewMediaItem?: (mediaItem: CreativeReportMediaItem) => void;
  totalRow?: Omit<CreativeReportRow, "ads">;
  preservePaginationState?: boolean;
  status: string;
  reportMetadata?: CreativeReportMetadata | null;
  serverPagination?: ServerPaginationState;
  onServerPageChange?: (page: number) => void;
  onServerPageSizeChange?: (pageSize: number) => void;
  isPaginationLoading?: boolean;
}

const AdsCreativeTable = ({
  data,
  columns,
  setGroupSheetOpen,
  setSelectedAdGroup,
  setPreviewMediaItem,
  totalRow,
  preservePaginationState = true,
  status = "success",
  reportMetadata,
  serverPagination,
  onServerPageChange,
  onServerPageSizeChange,
  isPaginationLoading = false,
}: AdsCreativeTableProps) => {
  const isScrolled = useRef<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});
  // Search input ref
  const searchInputRef = useRef<HTMLInputElement>(null);
  const shouldFocusSearch = useRef<boolean>(false);
  const cursorPositionRef = useRef<number>(0);

  // Simple search logic - single search for first primary column
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isSearchActive, setIsSearchActive] = useState<boolean>(false);

  // Find the first primary column
  const firstPrimaryColumn = columns.find((col) => col.is_primary === true)?.key;

  const selectedAccount = useGlobalStore((state) => state.selectedAccount);
  const [expandedHeaders, setExpandedHeaders] = useState<Record<string, boolean>>({});
  const anyExpanded = useMemo(
    () => Object.values(expandedHeaders).some(Boolean),
    [expandedHeaders],
  );
  const hasCompareDate = useMemo(
    () =>
      Boolean(
        reportMetadata?.compare_date?.start_date &&
          reportMetadata?.compare_date?.end_date,
      ),
    [reportMetadata?.compare_date?.start_date, reportMetadata?.compare_date?.end_date],
  );

  // Reset expanded headers when there's no compare date
  useEffect(() => {
    if (!hasCompareDate) {
      setExpandedHeaders({});
    }
  }, [hasCompareDate]);

  // Search utility functions
  const getSearchableValue = (ad: CreativeReportAd, columnKey: string): string => {
    const value = ad?.[columnKey];
    return value ? String(value as string) : "";
  };

  const isValueMatchingQuery = (value: string, query: string): boolean => {
    return value.toLowerCase().includes(query.toLowerCase());
  };

  const isRowMatchingSearch = useCallback(
    (ad: CreativeReportAd) => {
      if (!searchQuery.trim() || !isSearchActive) return true;

      const searchableValue = getSearchableValue(ad, firstPrimaryColumn || "");
      return isValueMatchingQuery(searchableValue, searchQuery);
    },
    [searchQuery, isSearchActive, firstPrimaryColumn],
  );

  // Search handlers
  const handleSearchClick = useCallback(() => {
    setIsSearchActive(true);
    shouldFocusSearch.current = true;
  }, []);

  const handleSearchClose = useCallback(() => {
    setIsSearchActive(false);
    setSearchQuery("");
  }, []);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        handleSearchClose();
      }
    },
    [handleSearchClose],
  );

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value;
      const target = e.target;
      // Update cursor position during typing
      cursorPositionRef.current = target.selectionStart || 0;
      setSearchQuery(query);
    },
    [],
  );

  const handleSearchInputSelect = useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      cursorPositionRef.current = target.selectionStart || 0;
    },
    [],
  );

  const handleSearchInputClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    cursorPositionRef.current = target.selectionStart || 0;
  }, []);

  // Focus search input only when first activated (not on re-renders)
  useEffect(() => {
    if (isSearchActive && shouldFocusSearch.current && searchInputRef.current) {
      searchInputRef.current.focus();
      shouldFocusSearch.current = false; // Reset the flag
    }
  }, [isSearchActive]);
  // Keep input focused when typing
  useEffect(() => {
    if (isSearchActive && searchInputRef.current) {
      // Keep focus active as long as search is active, regardless of query length
      const timeoutId = setTimeout(() => {
        if (searchInputRef.current && isSearchActive) {
          searchInputRef.current.focus();
          // Restore cursor position when refocusing
          const position = cursorPositionRef.current;
          if (position <= searchQuery.length) {
            searchInputRef.current.setSelectionRange(position, position);
          }
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery, isSearchActive]);

  // Filter data based on search query using useMemo to reduce re-renders
  const filteredData = useMemo(() => {
    if (!data) return [];

    if (!searchQuery.trim() || !isSearchActive) {
      return data;
    }

    const matchingRows: CreativeReportAd[] = [];
    const nonMatchingRows: CreativeReportAd[] = [];

    data.forEach((ad) => {
      const searchableValue = getSearchableValue(ad, firstPrimaryColumn || "");
      const isMatching = isValueMatchingQuery(searchableValue, searchQuery);

      if (isMatching) {
        matchingRows.push(ad);
      } else {
        nonMatchingRows.push(ad);
      }
    });

    // Combine: matching rows first, then non-matching rows
    return [...matchingRows, ...nonMatchingRows];
  }, [data, searchQuery, isSearchActive, firstPrimaryColumn]);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const calculateTotalWidth = () => {
    const baseWidth = 247;
    const expandedExtras = columns.reduce((extra, col) => {
      return (
        extra +
        (col.column_type === "number" && expandedHeaders[col.key] ? baseWidth * 2 : 0)
      );
    }, 0);
    const base = columns.reduce((total, col) => {
      if (col.is_primary) return total + 320;
      if (col.column_type === "number") return total + baseWidth;
      if (col.column_type === "text_ai_label") return total + baseWidth * 1.5;
      if (col.column_type === "text_ai_description") return total + baseWidth * 2;
      return total + baseWidth;
    }, 0);
    return base + expandedExtras;
  };

  const pin_cols = columns.filter((col) => col.is_primary === true).map((col) => col.key);

  // Custom sorting function to handle N/A values
  const customSortingFn = (
    rowA: Row<CreativeReportAd>,
    rowB: Row<CreativeReportAd>,
    columnId: string,
  ) => {
    const aValue = rowA.getValue(columnId);
    const bValue = rowB.getValue(columnId);

    const isNAValue = (val: unknown) => {
      const str = String(val);
      return str === "N/A" || str === "n/a" || str === "";
    };

    const aIsNA = isNAValue(aValue);
    const bIsNA = isNAValue(bValue);

    if (aIsNA && bIsNA) return 0;

    const column = table?.getColumn(columnId);
    const sortDirection = column?.getIsSorted();

    if (aIsNA && !bIsNA) {
      return sortDirection === "desc" ? -1 : 1;
    }
    if (!aIsNA && bIsNA) {
      return sortDirection === "desc" ? 1 : -1;
    }

    const aNum = Number(aValue);
    const bNum = Number(bValue);

    if (!isNaN(aNum) && !isNaN(bNum)) {
      return aNum - bNum;
    }

    return String(aValue).localeCompare(String(bValue));
  };

  const isCellLoading = (columnKey: string, value: string) => {
    if (columnKey === SPACER) return false;
    const v = String(value || "").toUpperCase();
    if (status === "partial") {
      return v === "__LOADING" || v === "__LOADING__" || v === "UNDEFINED";
    }
    return false;
  };

  const getMainColumnBgColor = (columnId: string, row: CreativeReportAd) => {
    if (columnId.endsWith("_previous") || columnId.endsWith("_change")) return undefined;
    // Only apply green background if there's compare date data
    if (!hasCompareDate) return undefined;
    const rawPercent = (row as Record<string, unknown>)[`${columnId}_percent_change`];
    const numeric = Number(rawPercent);
    if (isNaN(numeric)) return undefined;
    // Use raw percent; highlight only for positive values
    if (numeric <= 0) return undefined;
    if (numeric <= 20) return "#009B4D08";
    if (numeric <= 50) return "#009B4D0F";
    if (numeric <= 100) return "#009B4D1A";
    return "#009B4D29";
  };

  const getPinnedColumnStyle = <TData,>(
    column: Column<TData, unknown>,
    isHeader: boolean = false,
  ): CSSProperties => {
    const isPinned = column?.getIsPinned();
    const isMetric =
      (column.columnDef.meta as CreativeTableColumnMeta)?.type === "number";
    const isLabel =
      (column.columnDef.meta as CreativeTableColumnMeta)?.type === "text_ai_label";
    const isDescription =
      (column.columnDef.meta as CreativeTableColumnMeta)?.type === "text_ai_description";
    const isText = (column.columnDef.meta as CreativeTableColumnMeta)?.type === "text";
    const baseWidth = 247;
    const columnSize = column.getSize();
    const leftOffset = isPinned
      ? table
          .getAllFlatColumns()
          .slice(0, column.getIndex())
          .reduce((sum, c) => sum + c.getSize(), 0)
      : 0;

    return {
      position: isPinned ? "sticky" : "relative",
      zIndex: isPinned ? (isHeader ? 2 : 1) : 0,
      ...(isPinned
        ? {
            left: `${leftOffset}px`,
            width: `${columnSize}px`,
            minWidth: `${columnSize}px`,
            backgroundColor: isHeader ? "#ffffff" : undefined,
          }
        : isMetric
          ? {
              minWidth: `${columnSize || baseWidth}px`,
              width: `${columnSize || baseWidth}px`,
              maxWidth: `${columnSize || baseWidth}px`,
            }
          : isLabel
            ? {
                minWidth: `${columnSize || baseWidth * 1.5}px`,
                width: `${columnSize || baseWidth * 1.5}px`,
                maxWidth: `${columnSize || baseWidth * 1.5}px`,
              }
            : isDescription
              ? {
                  minWidth: `${columnSize || baseWidth * 2}px`,
                  width: `${columnSize || baseWidth * 2}px`,
                  maxWidth: `${columnSize || baseWidth * 2}px`,
                }
              : isText
                ? {
                    minWidth: `${columnSize || baseWidth}px`,
                    width: `${columnSize || baseWidth}px`,
                    maxWidth: `${columnSize || baseWidth}px`,
                  }
                : {}),
    };
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollLeft = e.currentTarget.scrollLeft;
    const wasScrolled = isScrolled.current;
    isScrolled.current = scrollLeft > 0;

    if (wasScrolled !== isScrolled.current) {
      const pinnedCells = e.currentTarget.querySelectorAll(".pinned-last");
      pinnedCells.forEach((cell) => {
        cell.classList.toggle("pinned-shadow", isScrolled.current);
      });
    }
  };

  const RenderToolTippedCell = ({
    cellText,
    headerText,
    align,
    isPinned,
    columnKey,
    info,
    className,
  }: {
    cellText: React.ReactNode;
    headerText: string;
    align: string;
    isPinned: string | boolean;
    columnKey: string;
    info?: string | null;
    className?: string;
  }) => {
    const value =
      React.isValidElement(cellText) && "getValue" in cellText.props
        ? String((cellText.props as { getValue: () => unknown }).getValue())
        : typeof cellText === "string"
          ? cellText
          : "";

    const isLoading = isCellLoading(columnKey, value);
    if (isLoading) {
      return (
        <div className="w-full px-2 pt-1">
          <Skeleton className="h-[23px]" />
        </div>
      );
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger className="w-full">
            <div
              className={cn(
                `text-[12px] whitespace-normal break-words
              ${align === "left" && "line-clamp-3"}
              text-${align}`,
                className,
              )}
            >
              {value === "N/A" ? "—" : cellText}
            </div>
          </TooltipTrigger>
          <Portal>
            <TooltipContent
              side="bottom"
              align="end"
              className={`text-[12px] max-w-[340px] text-left z-[60] ${
                isPinned
                  ? "bg-white shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10),_0px_1px_3px_0px_rgba(0,0,0,0.10)] border border-border-gray"
                  : ""
              }`}
              avoidCollisions
              collisionPadding={10}
            >
              {isPinned ? (
                <div className="bg-white text-black p-2">
                  <div className="mb-1 text-brand-primary-new font-[500]">
                    {headerText}
                  </div>
                  {isLoading ? (
                    <div className="flex items-center justify-center text-brand-primary-new">
                      <LuLoaderCircle className="w-4 h-4 animate-spin text-brand-primary-new" />
                    </div>
                  ) : (
                    <p className="whitespace-normal break-words text-brand-primary-new">
                      {info ?? cellText}
                    </p>
                  )}
                </div>
              ) : isLoading ? (
                <div className="flex items-center justify-center">
                  <LuLoaderCircle className="w-4 h-4 animate-spin text-brand-primary-new" />
                </div>
              ) : (
                <p className="whitespace-normal break-words text-white [&_*]:!text-white">
                  {info ?? cellText}
                </p>
              )}
            </TooltipContent>
          </Portal>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const RenderTextAiDescription = ({
    cellText,
    headerText,
    align,
    columnKey,
  }: {
    cellText: React.ReactNode;
    headerText: string;
    align: string;
    columnKey: string;
  }) => {
    const value =
      React.isValidElement(cellText) && "getValue" in cellText.props
        ? String((cellText.props as { getValue: () => unknown }).getValue())
        : typeof cellText === "string"
          ? cellText
          : "";

    const loading = isCellLoading(columnKey, value);
    if (loading) {
      return (
        <div className="w-full px-2 pt-1">
          <Skeleton className="h-[23px]" />
        </div>
      );
    }
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger className="w-full">
            <div
              className={cn(
                "text-[12px] whitespace-normal break-words leading-[16px]",
                align === "left" && "line-clamp-3",
              )}
              style={{ textAlign: align as "left" | "center" | "right" }}
            >
              {value === "N/A" ? "—" : cellText}
            </div>
          </TooltipTrigger>
          <Portal>
            <TooltipContent
              side="bottom"
              align="start"
              className={`flex flex-col gap-1 text-[12px] text-left z-[60] bg-white shadow-sm border border-border-gray min-w-[400px] max-w-[400px] p-3  max-h-[300px] overflow-y-auto pointer-events-auto`}
              onWheel={(e) => e.stopPropagation()}
              avoidCollisions
              alignOffset={25}
              collisionPadding={10}
            >
              <div className="text-brand-primary-800 text-[14px] font-medium leading-[24px] tracking-[0.02em]">
                {headerText}
              </div>
              <MarkdownFormatter
                content={value}
                className="text-brand-primary-new/80 text-[12px] font-normal leading-[22px] tracking-[0.02em]"
              />
            </TooltipContent>
          </Portal>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // Build columns with dynamic expansion
  const table_columns: ColumnDef<CreativeReportAd>[] = columns.flatMap((col) => {
    const isNumber = col.column_type === "number";
    const isPrimary = col.is_primary === true;
    const base: ColumnDef<CreativeReportAd> = {
      id: col.key,
      accessorKey: col.key,
      sortingFn: customSortingFn,
      size: isPrimary
        ? 320
        : isNumber
          ? 247
          : col.column_type === "text_ai_label"
            ? 247 * 1.5
            : col.column_type === "text_ai_description"
              ? 247 * 2
              : 247,
      enableResizing: true,
      minSize: isPrimary ? 285 : 100,
      maxSize: isPrimary ? 600 : 500,
      meta: {
        align: isNumber ? "right" : "left",
        label: col.label,
        type: col.column_type,
        suffix: col?.suffix ?? "",
        prefix: col?.prefix ?? "",
        info: col?.messages?.info,
        warning: col?.messages?.warning,
      } as CreativeTableColumnMeta,
      header: ({ column }: { column: Column<CreativeReportAd> }) => {
        const currentSort = column.getIsSorted();
        return (
          <div className={`flex items-center w-full pl-2 pr-3 min-w-0`}>
            <div className={`flex ${isNumber ? "order-1" : "order-2 ml-auto"}`}>
              <div
                className={cn(
                  "opacity-0 pointer-events-none group-hover/header:opacity-100 group-hover/header:pointer-events-auto transition-opacity mr-1",
                  currentSort && "opacity-100 pointer-events-auto",
                  !isNumber && "mr-0",
                )}
              >
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <SqButton
                        variant="icon"
                        hasOutline
                        bgColor="white"
                        hoverBgColor="#FBFBFB"
                        className={cn("h-7 w-7 shrink-0", !isNumber && "mr-0")}
                        onClick={() => {
                          if (currentSort === false) column.toggleSorting(false);
                          else if (currentSort === "asc") column.toggleSorting(true);
                          else column.clearSorting();
                        }}
                      >
                        {!currentSort ? (
                          <RiArrowUpDownLine size={14} color="#9AA3BD" />
                        ) : currentSort === "asc" ? (
                          <RiArrowDownLine size={14} color="#9AA3BD" />
                        ) : (
                          currentSort === "desc" && (
                            <RiArrowUpLine size={14} color="#9AA3BD" />
                          )
                        )}
                      </SqButton>
                    </TooltipTrigger>
                    <Portal>
                      <TooltipContent align="start" side="bottom">
                        {!currentSort
                          ? "Sort"
                          : currentSort === "asc"
                            ? "Ascending"
                            : currentSort === "desc" && "Descending"}
                      </TooltipContent>
                    </Portal>
                  </Tooltip>
                </TooltipProvider>
              </div>

              {isNumber && hasCompareDate && (
                <div className="opacity-0 pointer-events-none group-hover/header:opacity-100 group-hover/header:pointer-events-auto transition-opacity">
                  <TooltipProvider delayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <SqButton
                          variant="icon"
                          hasOutline
                          bgColor="white"
                          hoverBgColor="#FBFBFB"
                          className="h-7 w-7 shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedHeaders((prev) => ({
                              ...prev,
                              [col.key]: !prev[col.key],
                            }));
                          }}
                        >
                          {expandedHeaders[col.key] ? (
                            <RiArrowLeftWideLine size={14} color="#9AA3BD" />
                          ) : (
                            <RiArrowRightWideLine size={14} color="#9AA3BD" />
                          )}
                        </SqButton>
                      </TooltipTrigger>
                      <Portal>
                        <TooltipContent side="bottom" align="start">
                          {expandedHeaders[col.key] ? "Collapse" : "Expand"}
                        </TooltipContent>
                      </Portal>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
            </div>

            <div
              className={`flex gap-1 py-0 hover:text-none overflow-hidden min-w-0 items-center w-full  ${
                isNumber ? "order-2 ml-auto justify-end" : "order-1 justify-start"
              } ${
                col.is_primary && col.key === firstPrimaryColumn ? "justify-between" : ""
              }`}
            >
              <div className="flex items-center gap-1 min-w-0">
                {col?.messages?.warning && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild className="cursor-pointer">
                        <IconAlertTriangle
                          size={16}
                          color="#F18400"
                          className="shrink-0"
                        />
                      </TooltipTrigger>
                      <Portal>
                        <TooltipContent
                          side="top"
                          align="start"
                          collisionPadding={10}
                          className="max-w-[224px] h-auto text-wrap text-left whitespace-pre-line"
                        >
                          {col?.messages?.warning}
                        </TooltipContent>
                      </Portal>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {col?.sub_type === "EXTERNAL" && (
                  <IconDatabase size={16} color="#02185999" className="shrink-0" />
                )}
                {/* Show header label only when not searching */}
                {!(
                  col.is_primary &&
                  col.key === firstPrimaryColumn &&
                  isSearchActive
                ) && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild className="cursor-pointer min-w-0">
                        <span className="truncate font-medium text-[12px] max-w-full block">
                          {col.label}
                        </span>
                      </TooltipTrigger>
                      <Portal>
                        <TooltipContent side="top" align="start">
                          {col.label}
                        </TooltipContent>
                      </Portal>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              {/* Add search component only for the first primary column */}
              {col.is_primary &&
                col.key === firstPrimaryColumn &&
                (isSearchActive ? (
                  <div className="bg-white rounded-[8px] flex items-center gap-2 w-full squircle squircle-radius-[8px] squircle-smooth squircle-outline-[1px] squircle-outline-color-border-gray h-7 mr-1 px-1">
                    <RiSearchLine size={14} color="#9aa3bd" className="ml-1" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={handleSearchInputChange}
                      onClick={handleSearchInputClick}
                      onSelect={handleSearchInputSelect}
                      onKeyDown={handleSearchKeyDown}
                      placeholder={`Search ${col.label.toLowerCase()}`}
                      className="outline-none ring-0 focus:ring-0 focus:outline-none bg-transparent flex-1 py-1 text-[12px] font-normal rounded-md translate-y-[1px] h-6 p-0"
                      key="search-input"
                    />
                    <button onClick={handleSearchClose} className="p-1 rounded-md">
                      <RiCloseLine size={14} color="#9aa3bd" />
                    </button>
                  </div>
                ) : (
                  <SqButton
                    variant={"icon"}
                    bgColor="white"
                    hasOutline
                    onClick={handleSearchClick}
                    className="h-7 w-7 mr-1 opacity-0 group-hover/header:opacity-100"
                    hoverBgColor="#FBFBFB"
                  >
                    <RiSearchLine size={14} color="#9AA3BD" />
                  </SqButton>
                ))}
            </div>
          </div>
        );
      },
      cell: ({ row }: { row: Row<CreativeReportAd> }) => {
        const value = row.getValue(col.key);
        if (value === "") return value as string;
        const numValue = Number(value);
        if (isNumber && !isNaN(numValue)) {
          const baseLoading = isCellLoading(col.key, String(value));
          if (baseLoading) {
            return (
              <div className="w-full px-2 pt-1">
                <Skeleton className="h-[23px]" />
              </div>
            );
          }
          const percent = (row.original as Record<string, unknown>)[
            `${col.key}_percent_change`
          ];
          const isPercentLoading = isCellLoading(
            `${col.key}_percent_change`,
            typeof percent === "string" || typeof percent === "number"
              ? String(percent)
              : "",
          );
          return (
            <div className="flex flex-col items-end gap-1">
              <div className="text-[12px]">
                {formatNumberWithPrefixSuffix(numValue, col?.prefix, col?.suffix)}
              </div>
              {hasCompareDate && (
                <PercentageText
                  value={percent as number | string | null | undefined}
                  isLoading={isPercentLoading}
                />
              )}
            </div>
          );
        }
        return String(value);
      },
    };

    const defs: ColumnDef<CreativeReportAd>[] = [base];

    if (isNumber && hasCompareDate && expandedHeaders[col.key]) {
      defs.push({
        id: `${col.key}_previous`,
        accessorFn: (r) =>
          (r as unknown as Record<string, unknown>)[`${col.key}_previous`],
        sortingFn: customSortingFn,
        size: 247,
        enableResizing: true,
        minSize: 100,
        maxSize: 500,
        meta: {
          align: "right",
          label: "Previous",
          type: "number",
          suffix: col?.suffix ?? "",
          prefix: col?.prefix ?? "",
        } as CreativeTableColumnMeta,
        header: () =>
          expandedHeaders[col.key] ? null : (
            <span className="truncate font-medium text-[12px]">Previous</span>
          ),
        cell: ({ row }) => {
          const v = (row.original as Record<string, unknown>)[`${col.key}_previous`];
          const loading = isCellLoading(
            `${col.key}_previous`,
            typeof v === "string" || typeof v === "number" ? String(v) : "",
          );
          if (loading)
            return (
              <div className="w-full px-2 pt-1">
                <Skeleton className="h-[23px]" />
              </div>
            );
          const num = Number(v);
          return isNaN(num)
            ? typeof v === "string"
              ? v
              : ""
            : formatNumberWithPrefixSuffix(num, col?.prefix, col?.suffix);
        },
      });

      defs.push({
        id: `${col.key}_change`,
        accessorFn: (r) => (r as unknown as Record<string, unknown>)[`${col.key}_change`],
        sortingFn: customSortingFn,
        size: 247,
        enableResizing: true,
        minSize: 100,
        maxSize: 500,
        meta: {
          align: "right",
          label: "Change",
          type: "number",
          suffix: col?.suffix ?? "",
          prefix: col?.prefix ?? "",
        } as CreativeTableColumnMeta,
        header: () =>
          expandedHeaders[col.key] ? null : (
            <span className="truncate font-medium text-[12px]">Change</span>
          ),
        cell: ({ row }) => {
          const v = (row.original as Record<string, unknown>)[`${col.key}_change`];
          const loading = isCellLoading(
            `${col.key}_change`,
            typeof v === "string" || typeof v === "number" ? String(v) : "",
          );
          if (loading)
            return (
              <div className="w-full px-2 pt-1">
                <Skeleton className="h-[23px]" />
              </div>
            );
          const num = Number(v);
          return isNaN(num)
            ? typeof v === "string"
              ? v
              : ""
            : formatNumberWithPrefixSuffix(num, col?.prefix, col?.suffix);
        },
      });
    }

    return defs;
  });

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const handlePaginationChange = useCallback(
    (updater: Updater<PaginationState>) => {
      setPagination((prev) => {
        const newPagination = typeof updater === "function" ? updater(prev) : updater;
        if (serverPagination) {
          if (newPagination.pageSize !== prev.pageSize) {
            onServerPageSizeChange?.(newPagination.pageSize);
          } else if (newPagination.pageIndex !== prev.pageIndex) {
            onServerPageChange?.(newPagination.pageIndex + 1);
          }
        }
        return newPagination;
      });
    },
    [serverPagination, onServerPageChange, onServerPageSizeChange],
  );

  const table = useReactTable({
    data: filteredData || [],
    columns: [
      ...table_columns,
      ...(calculateTotalWidth() < containerWidth
        ? [
            {
              id: SPACER,
              accessorKey: SPACER,
              meta: {
                align: "left",
                label: "",
                type: SPACER,
              } as CreativeTableColumnMeta,
              maxSize: Number.MAX_SAFE_INTEGER,
              minSize: 0,
              enableResizing: false,
              header: () => null,
              cell: () => null,
              enableHiding: true,
            },
          ]
        : []),
    ],
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    manualPagination: !!serverPagination,
    ...(serverPagination ? { pageCount: serverPagination.totalPages } : {}),
    onPaginationChange: serverPagination ? handlePaginationChange : setPagination,
    columnResizeMode: "onChange" as const,
    enableColumnResizing: true,
    columnResizeDirection: "ltr",
    defaultColumn: {
      size: undefined,
    },
    onColumnSizingChange: setColumnSizing,
    state: {
      sorting: isSearchActive && searchQuery.trim() ? [] : sorting,
      pagination: serverPagination
        ? { pageIndex: serverPagination.page - 1, pageSize: serverPagination.pageSize }
        : pagination,
      columnSizing,
    },
    initialState: {
      columnPinning: {
        left: pin_cols,
      },
    },
    autoResetPageIndex: !preservePaginationState && !serverPagination,
  });

  // Spacer visibility based on current widths
  const updateSpacer = useCallback(() => {
    const totalWidth = table
      .getAllColumns()
      .filter((column) => column.id !== SPACER)
      .reduce((total, column) => total + column.getSize(), 0);
    const spacerColumn = table.getColumn(SPACER);
    if (spacerColumn) {
      spacerColumn.toggleVisibility(totalWidth < containerWidth);
    }
  }, [table, containerWidth]);

  useEffect(() => {
    updateSpacer();
  }, [updateSpacer, columnSizing, containerWidth]);

  return (
    <div ref={containerRef} className="relative">
      {isPaginationLoading && (
        <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center rounded-[12px]">
          <LuLoaderCircle className="w-5 h-5 animate-spin text-brand-primary-new/60" />
        </div>
      )}
      <style>
        {`
        .pinned-shadow {
          box-shadow: 8px 0 8px -4px rgba(228, 228, 231, 0.40);
        }
        .pinned-last {
          border-right: 1px solid #e5e7eb;
        }
        .pinned-cell {
          background-color: #ffffff !important;
        }
        tr:hover .pinned-cell {
          background-color: rgb(251,251,251) !important;
        }
        .resizer {
          position: absolute;
          right: 0;
          top: 0;
          height: 100%;
          width: 4px;
          background: rgba(0, 0, 0, 0.1);
          cursor: col-resize;
          user-select: none;
          touch-action: none;
          opacity: 0;
          transition: opacity 0.2s;
        }
        .resizer:hover { opacity: 1; }
        .resizer.isResizing { opacity: 1; }
        .table-container { width: 100%; overflow-x: auto; }
        table { table-layout: fixed !important; }
        `}
      </style>
      <div
        className="border border-border-gray rounded-[12px] overflow-hidden table-container"
        onScroll={handleScroll}
      >
        <Table
          className="border-separate border-spacing-0 cursor-default bg-white"
          style={{ width: "max-content", minWidth: "100%" }}
        >
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className="group/row hover:bg-[rgb(251,251,251)]"
              >
                {headerGroup.headers.map((header, index) => {
                  const isLastHeader = index === headerGroup.headers.length - 1;
                  const isFirstRow = true;
                  const columnId = header.column.id;
                  const baseId = columnId.replace(/(_previous|_change)$/, "");
                  const isSecondary =
                    hasCompareDate &&
                    (columnId.endsWith("_previous") || columnId.endsWith("_change"));
                  const isExpanded = expandedHeaders[baseId];
                  if (isExpanded && isSecondary) {
                    return null;
                  }
                  const colSpan = isExpanded && !isSecondary ? 3 : 1;
                  return (
                    <TableHead
                      key={header.id}
                      colSpan={colSpan}
                      style={{
                        ...getPinnedColumnStyle(header.column, true),
                        borderRight: "1px solid #E4E4E7",
                        borderBottom: "1px solid #E4E4E7",
                        ...(index === 0 && { borderLeft: "none" }),
                        ...(isLastHeader && { borderRight: "none" }),
                        ...(isFirstRow && { borderTop: "none" }),
                      }}
                      className={`p-0 text-brand-primary-new/80 group-hover/row:text-brand-primary-new group/header ${header.column.getIsLastColumn("left") ? "pinned-last" : ""}
                        ${header.column.getIsPinned() ? "pinned-cell" : ""}`}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      <div
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        className={`resizer ${header.column.getIsResizing() ? "isResizing" : ""}`}
                      />
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
            {anyExpanded && (
              <TableRow className="group/row hover:bg-[rgb(251,251,251)]">
                {table.getHeaderGroups()[0]?.headers.map((header, index) => {
                  const colId = header.column.id;
                  const meta = header.column.columnDef.meta as CreativeTableColumnMeta;
                  const isPrevious = hasCompareDate && colId.endsWith("_previous");
                  const isChange = hasCompareDate && colId.endsWith("_change");
                  return (
                    <TableHead
                      key={`sub-${header.id}`}
                      style={{
                        ...getPinnedColumnStyle(header.column, true),
                        borderRight: "1px solid #E4E4E7",
                        borderBottom: "1px solid #E4E4E7",
                        ...(index === 0 && { borderLeft: "none" }),
                      }}
                      className={`p-0 ${header.column.getIsLastColumn("left") ? "pinned-last" : ""}
                      ${header.column.getIsPinned() ? "pinned-cell" : ""}`}
                    >
                      {meta?.type === "number" ? (
                        <div className="px-2 py-1 text-[12px] text-brand-primary-new/60 leading-[16px]">
                          {isPrevious ? (
                            <div className="text-right">
                              {formatDateRange({
                                ...reportMetadata?.compare_date,
                                timezone: selectedAccount?.time_zone ?? "UTC",
                              })}
                            </div>
                          ) : isChange ? (
                            <div className="text-right">Change</div>
                          ) : (
                            <div className="text-right">
                              {formatDateRange({
                                ...reportMetadata?.date,
                                timezone: selectedAccount?.time_zone ?? "UTC",
                              })}
                            </div>
                          )}
                        </div>
                      ) : null}
                    </TableHead>
                  );
                })}
              </TableRow>
            )}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              <>
                {table.getRowModel().rows.map((row, rowIndex) => {
                  const ad = row.original;
                  const mediaItem = getFirstMedia(ad)[0];
                  const getClickAction = (row: CreativeReportAd) => {
                    return () => {
                      if (setGroupSheetOpen) {
                        setGroupSheetOpen(true);
                      }
                      if (setSelectedAdGroup) {
                        setSelectedAdGroup(row);
                      }
                      if (setPreviewMediaItem) {
                        setPreviewMediaItem({
                          ...mediaItem,
                          account_name: row.creative?.account_name || "",
                        });
                      }
                    };
                  };
                  return (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      className={cn(
                        "group/row hover:bg-[rgb(251,251,251)]",
                        !isRowMatchingSearch(row.original) && "opacity-50",
                      )}
                    >
                      {row.getVisibleCells().map((cell, cellIndex) => (
                        <TableCell
                          key={cell.id}
                          style={{
                            ...getPinnedColumnStyle(cell.column, false),
                            ...(getMainColumnBgColor(cell.column.id, row.original) && {
                              backgroundColor: getMainColumnBgColor(
                                cell.column.id,
                                row.original,
                              ),
                            }),
                            borderRight: "1px solid #E4E4E7",
                            borderBottom: "1px solid #E4E4E7",
                            ...(cellIndex === 0 && { borderLeft: "none" }),
                            ...(cell.column.getIsLastColumn() && { borderRight: "none" }),
                            ...(rowIndex === table.getRowModel().rows.length - 1 && {
                              borderBottom: "none",
                            }),
                          }}
                          className={`max-w-24 py-2 pl-2 pr-3 overflow-hidden relative 
                            text-[12px] text-brand-primary-new/80 font-normal group-hover/row:text-brand-primary-new
                            ${cell.column.getIsLastColumn("left") ? "pinned-last" : ""}
                            ${cell.column.getIsPinned() ? "pinned-cell" : ""}
                            text-${(cell.column.columnDef.meta as CreativeTableColumnMeta)?.align || "left"}`}
                        >
                          {cell.column.getIsFirstColumn("left") ? (
                            <RenderPinnedCell
                              mediaItem={mediaItem}
                              text={flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                              clickAction={getClickAction(row.original)}
                              total_ads={undefined}
                              mediaType={row.original.creative_type}
                            />
                          ) : (cell.column.columnDef.meta as CreativeTableColumnMeta)
                              .type === "text_ai_description" ? (
                            <RenderTextAiDescription
                              cellText={flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                              align={
                                (cell.column.columnDef.meta as CreativeTableColumnMeta)
                                  .align
                              }
                              headerText={
                                (cell.column.columnDef.meta as CreativeTableColumnMeta)
                                  .label
                              }
                              columnKey={cell.column.id}
                            />
                          ) : (
                            <RenderToolTippedCell
                              columnKey={cell.column.id}
                              cellText={flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                              isPinned={false}
                              headerText=""
                              align={
                                (cell.column.columnDef.meta as CreativeTableColumnMeta)
                                  .align
                              }
                            />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
                {totalRow && (
                  <TableRow className="group/row hover:bg-[rgb(251,251,251)]">
                    {table.getFlatHeaders().map((header, columnIndex) => {
                      const column = header.column;
                      return (
                        <TableCell
                          key={column.id}
                          style={{
                            ...getPinnedColumnStyle(column),
                            borderRight:
                              columnIndex === table.getFlatHeaders().length - 1
                                ? "none"
                                : undefined,
                          }}
                          className={`max-w-24 py-2 pl-2 pr-3 h-10 font-medium overflow-hidden 
                            border-t border-r border-border-gray
                            text-brand-primary-new/80 group-hover/row:text-brand-primary-new
                            ${columnIndex === 0 ? "rounded-bl-md" : ""} 
                            ${column.getIsPinned() ? "pinned-cell" : ""}
                            ${column.getIsLastColumn("left") ? "pinned-last" : ""}
                            text-${(column.columnDef.meta as CreativeTableColumnMeta).align}`}
                        >
                          {columnIndex === 0 ? (
                            <RenderToolTippedCell
                              cellText="Net Results"
                              isPinned={false}
                              align="left"
                              headerText={""}
                              columnKey=""
                              className="line-clamp-none"
                            />
                          ) : (
                            (() => {
                              const meta = column.columnDef
                                .meta as CreativeTableColumnMeta;
                              const colId = column.id;
                              const raw = totalRow[colId as keyof typeof totalRow];
                              if (raw === "") {
                                return (
                                  <RenderToolTippedCell
                                    cellText={""}
                                    isPinned={column.getIsPinned()}
                                    align={meta.align}
                                    headerText={meta.label}
                                    columnKey={colId}
                                  />
                                );
                              }
                              const isNumber = meta.type === "number";
                              const isSecondary =
                                hasCompareDate &&
                                (colId.endsWith("_previous") ||
                                  colId.endsWith("_change"));
                              if (!isNumber) {
                                const isLoadingText = isCellLoading(
                                  colId,
                                  typeof raw === "string" || typeof raw === "number"
                                    ? String(raw)
                                    : "",
                                );
                                const safeText = isLoadingText
                                  ? "__LOADING__"
                                  : typeof raw === "string"
                                    ? raw?.toUpperCase?.() === "UNDEFINED"
                                      ? ""
                                      : raw
                                    : typeof raw === "number"
                                      ? String(raw)
                                      : "";
                                return (
                                  <RenderToolTippedCell
                                    cellText={safeText}
                                    isPinned={column.getIsPinned()}
                                    align={meta.align}
                                    headerText={meta.label}
                                    columnKey={colId}
                                  />
                                );
                              }
                              if (isSecondary) {
                                const isLoadingSec = isCellLoading(
                                  colId,
                                  typeof raw === "string" || typeof raw === "number"
                                    ? String(raw)
                                    : "",
                                );
                                const num = Number(raw);
                                const text = isLoadingSec
                                  ? "__LOADING__"
                                  : raw === undefined || raw === null
                                    ? ""
                                    : !isNaN(num)
                                      ? formatNumberWithPrefixSuffix(
                                          num,
                                          meta.prefix,
                                          meta.suffix,
                                        )
                                      : typeof raw === "string"
                                        ? raw?.toUpperCase?.() === "UNDEFINED"
                                          ? ""
                                          : raw
                                        : "";
                                return (
                                  <RenderToolTippedCell
                                    cellText={text}
                                    isPinned={column.getIsPinned()}
                                    align={meta.align}
                                    headerText={meta.label}
                                    columnKey={colId}
                                  />
                                );
                              }
                              // Main metric total: value + percent change stacked
                              const isLoadingBase = isCellLoading(
                                colId,
                                typeof raw === "string" || typeof raw === "number"
                                  ? String(raw)
                                  : "",
                              );
                              const numValue = Number(raw);
                              const valueText = isLoadingBase
                                ? "__LOADING__"
                                : raw === undefined || raw === null
                                  ? ""
                                  : !isNaN(numValue)
                                    ? formatNumberWithPrefixSuffix(
                                        numValue,
                                        meta.prefix,
                                        meta.suffix,
                                      )
                                    : typeof raw === "string"
                                      ? raw?.toUpperCase?.() === "UNDEFINED"
                                        ? ""
                                        : raw
                                      : "";
                              const percentRaw =
                                totalRow[
                                  `${colId}_percent_change` as keyof typeof totalRow
                                ];
                              const isLoadingPct = isCellLoading(
                                `${colId}_percent_change`,
                                typeof percentRaw === "string" ||
                                  typeof percentRaw === "number"
                                  ? String(percentRaw)
                                  : "",
                              );

                              return (
                                <div className="flex flex-col items-end gap-1">
                                  <div className="text-[12px]">
                                    {valueText === "N/A" ? "—" : valueText}
                                  </div>
                                  {hasCompareDate && (
                                    <PercentageText
                                      value={
                                        percentRaw as number | string | null | undefined
                                      }
                                      isLoading={isLoadingPct}
                                    />
                                  )}
                                </div>
                              );
                            })()
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                )}
              </>
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination
        table={table}
        className="flex justify-between w-full text-brand-primary-new"
        buttonClass="hover:text-brand-primary-new"
      />
    </div>
  );
};

export default AdsCreativeTable;
